/**
 * DuplicateMergeModal Component
 *
 * Lists the groups of properties that duplicate detection calls the same address. Opened from
 * the Properties tab (every group) and from a mailshot's page (the groups in that mailshot).
 *
 * ⚠️ IT NO LONGER MERGES ANYTHING (Lee, 8 Oct 2026, EST-176). The name is kept so that the
 * two pages that open it did not have to change with it.
 *
 * Why merging is off: a group is made by a key that leaves out the flat, and that cannot see
 * when two listings carry different Land Registry addresses. And the merge itself
 * (src/utils/propertyMerge.js, run in the browser) copied NOTHING onto the record it kept but
 * the merged-away copies: not "do not contact", not mailshot membership, not the mailing
 * history. Counted on 8 Oct 2026: 66 of 825 groups were not plainly one home; and every one
 * of the 436 properties that were on a mailshot's own list while their own record did not say
 * so had been the kept record of a past merge (2,192 records merged away, January to March).
 *
 * "Merge all" went first; the single Merge went the same day. What comes back is a merge that
 * is a FUNCTION, compares flats, and carries the marks. Until then this box is for looking:
 * do not put a Merge control back on it, and do not call propertyMerge.js from a page.
 */

import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../../../firebase";
import {
  FaTimes,
  FaSpinner,
  FaChevronDown,
  FaChevronRight,
  FaCheck,
  FaExclamationTriangle,
  FaDatabase,
  FaHome,
  FaLayerGroup,
} from "react-icons/fa";
import { formatDate } from "../../../utils";
import "./DuplicateMergeModal.scss";

/** Why there is no Merge here. Shown in the box whenever it lists groups. */
export const MERGING_IS_OFF =
  "Merging is switched off until it can compare flat numbers and carry over “do not contact”, mailshot membership and mailing history. The groups are listed so that you can see them.";

export const DuplicateMergeModal = ({
  isOpen,
  onClose,
  duplicateGroups: initialGroups,
  title = "Duplicate properties",
  subtitle,
}) => {
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [loadingGroups, setLoadingGroups] = useState(false);

  // A group arrives either with its property records or with their ids only.
  useEffect(() => {
    async function loadGroups() {
      if (!initialGroups) {
        setLoadingGroups(false);
        return;
      }

      setLoadingGroups(true);

      try {
        const loaded = await Promise.all(
          initialGroups.map(async (group) => {
            let properties = group.properties || [];

            // If we only have property IDs, fetch the full property objects
            if (
              properties.length === 0 &&
              group.propertyIds &&
              group.propertyIds.length > 0
            ) {
              try {
                // Fetch properties in chunks of 30 (Firestore "in" query limit)
                const chunks = [];
                for (let i = 0; i < group.propertyIds.length; i += 30) {
                  chunks.push(group.propertyIds.slice(i, i + 30));
                }

                const fetchedProperties = [];
                for (const chunk of chunks) {
                  const q = query(
                    collection(db, "discovery_properties"),
                    where("__name__", "in", chunk)
                  );
                  const snapshot = await getDocs(q);
                  snapshot.docs.forEach((doc) => {
                    fetchedProperties.push({ id: doc.id, ...doc.data() });
                  });
                }
                properties = fetchedProperties;
              } catch (error) {
                console.error("Error fetching properties for group:", error);
              }
            }

            return { ...group, properties };
          })
        );

        setDuplicateGroups(loaded);
      } finally {
        setLoadingGroups(false);
      }
    }

    loadGroups();
  }, [initialGroups]);

  if (!isOpen) return null;

  const toggleGroup = (index) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  // Every record of a group beyond its first is a "duplicate" in the count the pages show.
  const totalDuplicates = duplicateGroups.reduce(
    (sum, group) => sum + Math.max(0, (group.properties?.length || 0) - 1),
    0
  );

  return (
    <div className="duplicate-merge-overlay" onClick={onClose}>
      <div
        className="duplicate-merge-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="duplicate-merge-modal__header">
          <div className="header-content">
            <div className="header-icon">
              <FaLayerGroup />
            </div>
            <div className="header-text">
              <h2>{title}</h2>
              <p className="subtitle">
                {subtitle || (
                  <>
                    {duplicateGroups.length} address{duplicateGroups.length !== 1 ? "es" : ""} with{" "}
                    {totalDuplicates} duplicate{totalDuplicates !== 1 ? "s" : ""}
                  </>
                )}
              </p>
            </div>
          </div>
          <button className="close-button" onClick={onClose} aria-label="Close">
            <FaTimes />
          </button>
        </div>

        {/* Body */}
        <div className="duplicate-merge-modal__body">
          {loadingGroups ? (
            <div className="empty-state">
              <FaSpinner className="spinner" style={{ fontSize: "3rem", color: "#3b82f6" }} />
              <h3>Loading Duplicate Groups...</h3>
              <p>Please wait while we fetch property details.</p>
            </div>
          ) : duplicateGroups.length === 0 ? (
            <div className="empty-state">
              <FaCheck className="success-icon" />
              <h3>No duplicate groups</h3>
              <p>No address is held more than once here.</p>
            </div>
          ) : (
            <div className="duplicate-groups">
              <div className="merge-explanation" role="note" data-testid="merging-is-off">
                <FaExclamationTriangle />
                <span>{MERGING_IS_OFF}</span>
              </div>
              {duplicateGroups.map((group, index) => {
                const isExpanded = expandedGroups.has(index);
                const first = group.properties[0];

                return (
                  <div key={group.hash || index} className="duplicate-group">
                    {/* Group Header */}
                    <div
                      className="duplicate-group__header"
                      onClick={() => toggleGroup(index)}
                    >
                      <div className="expand-icon">
                        {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                      </div>

                      <div className="group-info">
                        <div className="address">
                          {group.address || first?.address || "Unknown Address"}
                        </div>
                        <div className="meta">
                          <span className="postcode">
                            {group.postcode || first?.postcode}
                          </span>
                          <span className="count">
                            {group.properties.length} properties
                          </span>
                          {group.properties.some((p) => p.landRegistryRecord) && (
                            <span className="lr-badge">
                              <FaDatabase /> LR Data
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Expanded Content */}
                    {isExpanded && (
                      <div className="duplicate-group__body">
                        <div className="properties-list">
                          {group.properties.map((property) => (
                            <div key={property.id} className="property-card duplicate">
                              <div className="property-card__content">
                                <div className="property-address">
                                  <FaHome />
                                  <span>{property.address || "No address"}</span>
                                </div>

                                <div className="property-meta">
                                  <div className="meta-item">
                                    <span className="label">ID:</span>
                                    <span className="value mono">
                                      {property.id.slice(0, 12)}...
                                    </span>
                                  </div>
                                  <div className="meta-item">
                                    <span className="label">Postcode:</span>
                                    <span className="value">
                                      {property.postcode || "N/A"}
                                    </span>
                                  </div>
                                  <div className="meta-item">
                                    <span className="label">Added:</span>
                                    <span className="value">
                                      {property.dateAdded
                                        ? formatDate(property.dateAdded)
                                        : "Unknown"}
                                    </span>
                                  </div>
                                  {property.landRegistryRecord && (
                                    <div className="meta-item highlight">
                                      <FaDatabase />
                                      <span className="value">Has Land Registry</span>
                                    </div>
                                  )}
                                  {property.mergedPropertyIds?.length > 0 && (
                                    <div className="meta-item">
                                      <span className="label">Already merged:</span>
                                      <span className="value">
                                        {property.mergedPropertyIds.length} properties
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="duplicate-merge-modal__footer">
          <div className="footer-info">
            <FaExclamationTriangle />
            <span>Nothing here changes a record. This list is for looking.</span>
          </div>

          <div className="footer-actions">
            <button className="cancel-button" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

DuplicateMergeModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  duplicateGroups: PropTypes.arrayOf(
    PropTypes.shape({
      hash: PropTypes.string,
      address: PropTypes.string,
      postcode: PropTypes.string,
      properties: PropTypes.arrayOf(PropTypes.object),
      propertyIds: PropTypes.arrayOf(PropTypes.string),
    })
  ).isRequired,
  title: PropTypes.string,
  subtitle: PropTypes.string,
};

export default DuplicateMergeModal;
